import {
  getCurrentFacilityId,
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
  userLoggedInFacilityType,
} from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import SingleTabWithLink, {
  SingleTabWithLinkProps,
} from "../../components/singleTabWithLink/singleTabWithLink";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import {
  clearDataFromStorage,
  logOutUserFromFirebase,
} from "../../redux/slice/firebaseAuth/logOutThunk";
import { getAllProviderDetailsForClinic } from "../../redux/slice/providerSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import AnalyticsFilledIcon from "../../svg/analyticsFilledIcon";
import AnalyticsIcon from "../../svg/analyticsIcon";
import ClaimsIcon from "../../svg/claims";
import ClaimsIconFilled from "../../svg/claimsIconFilled";
import ConfigFilledIcon from "../../svg/configFilledIcon";
import ConfigIcon from "../../svg/configIcon";
import ExclamationWithoutFill from "../../svg/exclaimationWithoutFill";
import ExclamationWithFillColor from "../../svg/exclamation-with-fill-color";
import Exit from "../../svg/exit";
import RisaLogoWhite from "../../svg/risa-logo-white";
import WorklistIcon from "../../svg/worklistIcon";
import WorklistIconFilled from "../../svg/worklistIconFilled";
import { logButtonClickEvent, ParamName } from "../../utils/events";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";

const HeaderVersion2 = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const [tabsList, setTabsList] = useState<SingleTabWithLinkProps[]>([]);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const navigationType = useNavigationType();

  const orgId = getOrgIdForFetchExternalWorklist();

  const currentFacilityId = getCurrentFacilityId() || "";
  const facilityType = userLoggedInFacilityType(currentFacilityId);
  const isExternalFacility = facilityType === "external";

  useEffect(() => {
    if (user) {
      generateTabs(isExternalFacility);
    }
  }, [user, isExternalFacility]);

  useEffect(() => {
    if (providerDetails) {
      dispatch(
        getAllProviderDetailsForClinic(providerDetails.FacilityId || ""),
      );
    }
  }, [providerDetails, dispatch]);

  useEffect(() => {
    if (tabsList && tabsList.length > 0 && navigationType !== "POP") {
      navigate(`/${tabsList[0].id}/`);
    } else if (tabsList && tabsList.length > 0 && location.pathname === "/") {
      navigate(`/${tabsList[0].id}/`);
    }
  }, [tabsList]);

  const generateTabs = (isExternal: boolean) => {
    let tabs;

    switch (true) {
      case isExternal:
        tabs = [
          {
            id: "external-analytics",
            label: "Analytics",
            defaultIcon: <AnalyticsIcon />,
            selectedIcon: <AnalyticsFilledIcon />,
          },
          {
            id: "external-worklist",
            label: "Orders",
            defaultIcon: <WorklistIcon />,
            selectedIcon: <WorklistIconFilled />,
          },
          {
            id: "external-escalations",
            label: "Escalations",
            defaultIcon: (
              <ExclamationWithoutFill
                height="20"
                width="20"
                strokeColor="#999999"
              />
            ),
            selectedIcon: (
              <ExclamationWithFillColor
                fillColor="white"
                height="20"
                width="20"
              />
            ),
          },
        ];

        break;

      default:
        tabs = [
          {
            id: "worklists",
            label: "Worklists",
            defaultIcon: <WorklistIcon />,
            selectedIcon: <WorklistIconFilled />,
          },
          {
            id: "claims-check",
            label: "Claims",
            defaultIcon: <ClaimsIcon />,
            selectedIcon: <ClaimsIconFilled />,
          },
          {
            id: "eligibilty-check",
            label: "Check EV",
            defaultIcon: <ClaimsIcon />,
            selectedIcon: <ClaimsIconFilled />,
          },
          {
            id: "external-analytics",
            label: "Analytics",
            defaultIcon: <AnalyticsIcon />,
            selectedIcon: <AnalyticsFilledIcon />,
          },
        ];

        break;
    }

    // Add Config tab for admins on non-external facilities
    if ((isCurrentUserAdmin() || isCurrentUserSuperAdmin()) && !isExternal) {
      tabs.push({
        id: "configurations",
        label: "Config",
        defaultIcon: <ConfigIcon />,
        selectedIcon: <ConfigFilledIcon />,
      });
    }

    setTabsList(tabs);
  };

  const logOutUser = async () => {
    await apiWorkerManager.enqueueAndProcess({
      event_name: EventsName.LOGOUT,
      patient_id: "",
      user_id: user?.id,
      user_email: user?.email || "",
      order_id: "",
      org_id: orgId,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.WORKLIST,
      },
    });
    logButtonClickEvent(ParamName.LOGOUT);
    dispatch(logOutUserFromFirebase());
    clearDataFromStorage();
  };

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="header--logo">
        <RisaLogoWhite height="52" width="52" />
      </div>
      <div className="header--worklist_tabs--container flex flex-1 flex-col gap-5">
        {tabsList.map((tab) => (
          <SingleTabWithLink
            key={tab.id}
            id={tab.id}
            label={tab.label}
            defaultIcon={tab.defaultIcon}
            selectedIcon={tab.selectedIcon}
          />
        ))}
      </div>
      <div className="header--actions-container mb-6 flex flex-col items-center justify-center gap-6">
        <div
          className="header-container--actions--profile-pic h-6 w-6 hover:scale-105 hover:cursor-pointer"
          onClick={() => {
            apiWorkerManager.enqueueAndProcess({
              event_name: EventsName.SETTINGS_CLICKED,
              patient_id: "",
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              order_id: "",
              org_id: orgId ?? "",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.SETTINGS,
              },
            });
            navigate("/settings");
          }}
        >
          <img
            src={providerDetails?.ProfilePicUrl || "/user.png"}
            alt="profile-pic"
            className="header-container--actions--profile-pic__image"
          />
        </div>
        <div className="header-container--actions--profile-pic--badge hover:scale-105 hover:cursor-pointer">
          <Exit onClick={logOutUser} stroke="white" />
        </div>
      </div>
    </div>
  );
};

export default HeaderVersion2;
